import {
  createInitialGameState,
  isGameState,
  type GameState,
  type GameStatus,
} from '../core/gameState'
import {
  isNationStateRecord,
  isRegionControllerRecord,
} from '../core/economy'
import {
  createInitialConstructionState,
  isConstructionState,
} from '../core/construction'
import {
  createInitialResearchState,
  isNationResearchRecord,
} from '../core/research'
import {
  createInitialSpaceProgramState,
  migrateLegacySpaceProgramRecord,
} from '../core/spaceProgram'
import {
  createGameSettings,
} from '../core/gameSettings'
import { createInitialStrategicLog } from '../core/strategicLog'
import { isActiveMajorPowerId } from '../data/regions'

export const SAVE_FORMAT = 'skyward-race-save'
export const SAVE_VERSION = 6 as const
export const AUTOSAVE_KEY = 'skyward-race.autosave.v6'
export const BACKUP_KEY = 'skyward-race.autosave.backup.v6'
const LEGACY_AUTOSAVE_KEYS = [
  'skyward-race.autosave.v5',
  'skyward-race.autosave.v4',
  'skyward-race.autosave.v3',
  'skyward-race.autosave.v2',
  'skyward-race.autosave.v1',
] as const
const LEGACY_BACKUP_KEYS = [
  'skyward-race.autosave.backup.v5',
  'skyward-race.autosave.backup.v4',
  'skyward-race.autosave.backup.v3',
  'skyward-race.autosave.backup.v2',
  'skyward-race.autosave.backup.v1',
] as const

export interface SaveEnvelope {
  format: typeof SAVE_FORMAT
  saveVersion: typeof SAVE_VERSION
  game: GameState
}

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export type AutosaveLoadResult =
  | { kind: 'loaded'; state: GameState; migrated: boolean }
  | { kind: 'empty' }
  | { kind: 'invalid'; error: string }

export function serializeGameSave(state: GameState): string {
  const envelope: SaveEnvelope = {
    format: SAVE_FORMAT,
    saveVersion: SAVE_VERSION,
    game: state,
  }

  return JSON.stringify(envelope, null, 2)
}

export function parseGameSave(raw: string): GameState {
  let parsed: unknown

  try {
    parsed = JSON.parse(raw) as unknown
  } catch {
    throw new Error('存档不是有效的 JSON 文件。')
  }

  if (!isRecord(parsed)) {
    throw new Error('存档结构无效。')
  }

  if (parsed.format !== SAVE_FORMAT) {
    throw new Error('这不是《苍穹竞逐》的存档文件。')
  }

  if (parsed.saveVersion === SAVE_VERSION) {
    if (!isGameState(parsed.game)) {
      throw new Error('存档中的游戏状态无效或已损坏。')
    }

    return cloneGameState(parsed.game)
  }

  if (parsed.saveVersion === 5) {
    return migrateV5GameState(parsed.game)
  }

  if (parsed.saveVersion === 4) {
    return migrateV4GameState(parsed.game)
  }

  if (parsed.saveVersion === 3) {
    return migrateV3GameState(parsed.game)
  }

  if (parsed.saveVersion === 2) {
    return migrateV2GameState(parsed.game)
  }

  if (parsed.saveVersion === 1) {
    return migrateV1GameState(parsed.game)
  }

  throw new Error('存档版本与当前游戏不兼容。')
}

export function saveAutosave(storage: StorageLike, state: GameState): void {
  const nextSave = serializeGameSave(state)
  const currentSave = storage.getItem(AUTOSAVE_KEY)

  if (currentSave !== null) {
    storage.setItem(BACKUP_KEY, currentSave)
  }

  storage.setItem(AUTOSAVE_KEY, nextSave)
}

export function loadAutosave(storage: StorageLike): AutosaveLoadResult {
  const currentRaw = storage.getItem(AUTOSAVE_KEY)

  if (currentRaw !== null) {
    try {
      return {
        kind: 'loaded',
        state: parseGameSave(currentRaw),
        migrated: false,
      }
    } catch (error) {
      return {
        kind: 'invalid',
        error: getErrorMessage(error),
      }
    }
  }

  for (const legacyKey of LEGACY_AUTOSAVE_KEYS) {
    const legacyRaw = storage.getItem(legacyKey)
    if (legacyRaw === null) {
      continue
    }

    try {
      return {
        kind: 'loaded',
        state: parseGameSave(legacyRaw),
        migrated: true,
      }
    } catch (error) {
      return {
        kind: 'invalid',
        error: getErrorMessage(error),
      }
    }
  }

  return { kind: 'empty' }
}

export function clearAutosave(storage: StorageLike): void {
  storage.removeItem(AUTOSAVE_KEY)
  storage.removeItem(BACKUP_KEY)

  for (const key of [...LEGACY_AUTOSAVE_KEYS, ...LEGACY_BACKUP_KEYS]) {
    storage.removeItem(key)
  }
}

function migrateV5GameState(value: unknown): GameState {
  if (!isRecord(value)) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  const migratedSpaceProgram = migrateLegacySpaceProgramRecord(
    value.spaceProgram,
  )

  if (
    value.schemaVersion !== 5 ||
    typeof value.turn !== 'number' ||
    !Number.isInteger(value.turn) ||
    value.turn < 1 ||
    !isGameStatus(value.status) ||
    !(value.playerNationId === null || isActiveMajorPowerId(value.playerNationId)) ||
    !(value.winnerNationId === null || isActiveMajorPowerId(value.winnerNationId)) ||
    !isNationStateRecord(value.nations) ||
    !isRegionControllerRecord(value.regionControllers) ||
    !isConstructionState(value.construction) ||
    !isNationResearchRecord(value.research) ||
    migratedSpaceProgram === null
  ) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  return {
    schemaVersion: 6,
    turn: value.turn,
    status: value.status,
    playerNationId: value.playerNationId,
    winnerNationId: value.winnerNationId,
    settings: createGameSettings('standard'),
    nations: clone(value.nations) as GameState['nations'],
    regionControllers: {
      ...value.regionControllers,
    },
    construction: clone(value.construction) as GameState['construction'],
    research: clone(value.research) as GameState['research'],
    spaceProgram: migratedSpaceProgram,
    strategicLog: createInitialStrategicLog(),
  }
}

function migrateV4GameState(value: unknown): GameState {
  if (!isRecord(value)) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  if (
    value.schemaVersion !== 4 ||
    typeof value.turn !== 'number' ||
    !Number.isInteger(value.turn) ||
    value.turn < 1 ||
    !isGameStatus(value.status) ||
    !(value.playerNationId === null || isActiveMajorPowerId(value.playerNationId)) ||
    !isNationStateRecord(value.nations) ||
    !isRegionControllerRecord(value.regionControllers) ||
    !isConstructionState(value.construction) ||
    !isNationResearchRecord(value.research)
  ) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  return {
    schemaVersion: 6,
    turn: value.turn,
    status: value.status,
    playerNationId: value.playerNationId,
    winnerNationId: null,
    settings: createGameSettings('standard'),
    nations: clone(value.nations) as GameState['nations'],
    regionControllers: {
      ...value.regionControllers,
    },
    construction: clone(value.construction) as GameState['construction'],
    research: clone(value.research) as GameState['research'],
    spaceProgram: createInitialSpaceProgramState(),
    strategicLog: createInitialStrategicLog(),
  }
}

function migrateV3GameState(value: unknown): GameState {
  if (!isRecord(value)) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  if (
    value.schemaVersion !== 3 ||
    typeof value.turn !== 'number' ||
    !Number.isInteger(value.turn) ||
    value.turn < 1 ||
    !isGameStatus(value.status) ||
    !(value.playerNationId === null || isActiveMajorPowerId(value.playerNationId)) ||
    !isNationStateRecord(value.nations) ||
    !isRegionControllerRecord(value.regionControllers) ||
    !isConstructionState(value.construction)
  ) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  return {
    schemaVersion: 6,
    turn: value.turn,
    status: value.status,
    playerNationId: value.playerNationId,
    winnerNationId: null,
    settings: createGameSettings('standard'),
    nations: clone(value.nations) as GameState['nations'],
    regionControllers: {
      ...value.regionControllers,
    },
    construction: clone(value.construction) as GameState['construction'],
    research: createInitialResearchState(),
    spaceProgram: createInitialSpaceProgramState(),
    strategicLog: createInitialStrategicLog(),
  }
}

function migrateV2GameState(value: unknown): GameState {
  if (!isRecord(value)) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  if (
    value.schemaVersion !== 2 ||
    typeof value.turn !== 'number' ||
    !Number.isInteger(value.turn) ||
    value.turn < 1 ||
    !isGameStatus(value.status) ||
    !(value.playerNationId === null || isActiveMajorPowerId(value.playerNationId)) ||
    !isNationStateRecord(value.nations) ||
    !isRegionControllerRecord(value.regionControllers)
  ) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  return {
    schemaVersion: 6,
    turn: value.turn,
    status: value.status,
    playerNationId: value.playerNationId,
    winnerNationId: null,
    settings: createGameSettings('standard'),
    nations: clone(value.nations) as GameState['nations'],
    regionControllers: {
      ...value.regionControllers,
    },
    construction: createInitialConstructionState(),
    research: createInitialResearchState(),
    spaceProgram: createInitialSpaceProgramState(),
    strategicLog: createInitialStrategicLog(),
  }
}

function migrateV1GameState(value: unknown): GameState {
  if (!isRecord(value)) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  const turn = value.turn
  const status = value.status
  const playerNationId = value.playerNationId

  if (
    value.schemaVersion !== 1 ||
    typeof turn !== 'number' ||
    !Number.isInteger(turn) ||
    turn < 1 ||
    !isGameStatus(status)
  ) {
    throw new Error('旧版存档中的游戏状态无效。')
  }

  const migrated = createInitialGameState()
  migrated.turn = turn
  migrated.status = status

  if (playerNationId === null || status === 'setup') {
    migrated.playerNationId = null
  } else if (isActiveMajorPowerId(playerNationId)) {
    migrated.playerNationId = playerNationId
  } else if (typeof playerNationId === 'string' && playerNationId.length > 0) {
    migrated.playerNationId = 'starsea'
  } else {
    throw new Error('旧版存档中的玩家国家字段无效。')
  }

  return migrated
}

function clone(value: unknown): unknown {
  return JSON.parse(JSON.stringify(value)) as unknown
}

function cloneGameState(state: GameState): GameState {
  return clone(state) as GameState
}

function isGameStatus(value: unknown): value is GameStatus {
  return (
    value === 'setup' ||
    value === 'running' ||
    value === 'won' ||
    value === 'lost'
  )
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : '未知存档错误。'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
